/**
 * Record cancellation on a purchase order (docs/features/order-to-invoice/
 * 46_fulfilment_routes_step3_update_spec.md §5, 47_views_and_frontend_step3_update_spec.md
 * §4): the ⋯ menu's dialog. One reason and a confirm, as round 19's mock draws
 * it (title, description, label, placeholder, buttons); the choice of the
 * whole purchase order or some of its lines is added in the same style, shown
 * only when more than one line is open. Which lines are open, and the route it
 * posts to, are the purchase order view's `record_cancellation`.
 *
 * The purchase ledger does not yet learn of a cancelled line (doc 69): the
 * description says what happens, a ticket to finance when a ledger PO exists,
 * in place of the mock's "pushed to the purchase ledger".
 *
 * A held purchase order is a manager's or admin's to cancel (#94): the view
 * sends no route and says why, and the dialog says so and offers no confirm.
 * The server's refusal, bound to the field it names, replaces the checks made
 * as typed; `onRecord` resolves to the refusal to show, or null once done.
 */
import { useId, useState } from "react";
import type {
  RecordCancellation,
  RecordPurchaseOrderCancellationRequest,
} from "@shared-core/data/order-to-invoice-types";
import type { OrderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";
import { Field, FailureBanner } from "./OrderDialogs";

type Scope = "whole" | "lines";

export function RecordCancellationDialog({
  purchaseOrderNo,
  supplierName,
  isPlaced,
  cancellation,
  busy,
  onClose,
  onRecord,
}: {
  purchaseOrderNo: string;
  supplierName: string;
  /** Placed with the supplier already: it has to be cancelled with them first. */
  isPlaced: boolean;
  cancellation: RecordCancellation;
  busy: boolean;
  onClose: () => void;
  onRecord: (
    input: RecordPurchaseOrderCancellationRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const { lines, blocked_reason: blockedReason } = cancellation;
  const [scope, setScope] = useState<Scope>("whole");
  const [chosen, setChosen] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const offersLines = lines.length > 1;
  const isLines = offersLines && scope === "lines";
  const linesError =
    failure?.fieldErrors.purchase_order_line_ids ??
    (touched && isLines && chosen.length === 0
      ? "Choose the lines to cancel."
      : undefined);
  const reasonError =
    failure?.fieldErrors.reason ??
    (touched && !reason.trim() ? "Enter why it is cancelled." : undefined);
  const title = isLines
    ? `Record ${chosen.length === 1 ? "a line" : "lines"} of ${purchaseOrderNo} as already cancelled with the supplier`
    : `Record ${purchaseOrderNo} as already cancelled with the supplier`;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isPlaced
              ? `It was placed with ${supplierName}: cancel it with them first. If the purchase ledger has it, finance get a ticket to unwind it there. Each order line it was for needs a new route.`
              : `It isn't placed yet. If the purchase ledger has it, finance get a ticket to unwind it there. Each order line it was for needs a new route.`}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (blockedReason || !reason.trim()) return;
            if (isLines && chosen.length === 0) return;
            const refused = await onRecord({
              reason: reason.trim(),
              ...(isLines ? { purchase_order_line_ids: chosen } : {}),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          {blockedReason && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{blockedReason}</AlertDescription>
            </Alert>
          )}
          <FailureBanner failure={failure} />
          {offersLines && (
            <fieldset
              className="space-y-2"
              aria-invalid={linesError ? true : undefined}
              aria-describedby={linesError ? `${id}-lines-error` : undefined}
            >
              <legend className="text-sm font-medium">Cancel</legend>
              <RadioGroup
                value={scope}
                onValueChange={(v) => setScope(v as Scope)}
                aria-label="Cancel"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="whole" id={`${id}-whole`} />
                  <Label htmlFor={`${id}-whole`}>
                    The whole purchase order
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="lines" id={`${id}-lines`} />
                  <Label htmlFor={`${id}-lines`}>Only some of its lines</Label>
                </div>
              </RadioGroup>
              {isLines && (
                <div
                  role="group"
                  aria-label="Lines"
                  className="space-y-1.5 rounded-md border border-border p-2"
                >
                  {lines.map((line) => {
                    const checkboxId = `${id}-line-${line.id}`;
                    return (
                      <div key={line.id} className="flex items-center gap-2">
                        <Checkbox
                          id={checkboxId}
                          checked={chosen.includes(line.id)}
                          aria-invalid={linesError ? true : undefined}
                          onCheckedChange={(checked) =>
                            setChosen(
                              checked === true
                                ? [...chosen, line.id]
                                : chosen.filter((c) => c !== line.id),
                            )
                          }
                        />
                        <Label htmlFor={checkboxId} className="font-normal">
                          Line {line.line_no} · {line.description} ·{" "}
                          {formatQuotedPrice(line.committed_value_pence)}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              )}
              {linesError && (
                <p id={`${id}-lines-error`} className="text-sm text-red-600">
                  {linesError}
                </p>
              )}
            </fieldset>
          )}
          <Field
            id={`${id}-reason`}
            label="Why is it cancelled"
            hint="The supplier sees this"
            error={reasonError}
          >
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={reason}
                placeholder="Solopress can't make the 4 Oct despatch; moving to PDQ"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy || !!blockedReason}>
              Record cancellation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
